import { Platform, Dimensions } from 'react-native';
import { workClientEnvironment } from '@/lib/appDistribution';
import { getCachedDeviceObservation, ensureDeviceObservation } from '@/lib/deviceFingerprint';
import { getBrowserPanelSession } from '@/lib/browserPanel';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { hasNativeAnalyticsConsent, useNativeAnalyticsConsent } from '@/store/nativeAnalyticsConsent';
// Facebook and Reddit pixel helpers were removed 2026-09 when both pixels
// were retired. Matomo and the Whop pixel remain.
// ---------------------------------------------------------------------------
// Matomo configuration
// ---------------------------------------------------------------------------
const MATOMO_URL = 'https://matomo.gab.com/matomo.php';
const SITE_ID = '1';
const VISITOR_ID_STORAGE_KEY = '@matomo_visitor_id';
const CONTENT_BASE = 'https://app.gab.ai';
const DISPATCH_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Native tracker state (iOS / Android only)
// ---------------------------------------------------------------------------
let _visitorId = null;
let _userId = null;
let _initPromise = null;
let _eventQueue = [];
let _dispatchTimer = null;
let _customDimensions = {};

const generateVisitorId = () => {
  const hex = '0123456789abcdef';
  let id = '';
  for (let i = 0; i < 16; i++) id += hex[Math.floor(Math.random() * 16)];
  return id;
};

const ensureVisitorId = () => {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      const stored = await AsyncStorage.getItem(VISITOR_ID_STORAGE_KEY);
      if (stored && /^[0-9a-f]{16}$/.test(stored)) {
        _visitorId = stored;
      } else {
        _visitorId = generateVisitorId();
        await AsyncStorage.setItem(VISITOR_ID_STORAGE_KEY, _visitorId);
      }
    } catch {
      _visitorId = _visitorId || generateVisitorId();
    }
    return _visitorId;
  })();

  return _initPromise;
};

const buildUserAgent = () => {
  try {
    const os = Device.osName || Platform.OS;
    const osVer = Device.osVersion || Platform.Version;
    const model = Device.modelName || 'Unknown';
    const appVer = Constants.nativeAppVersion || '1.0.0';
    return `GabAI/${appVer} (${Platform.OS === 'ios' ? 'iOS' : 'Android'}; ${model}; ${os} ${osVer})`;
  } catch {
    return `GabAI/1.0.0 (${Platform.OS})`;
  }
};

const getScreenResolution = () => {
  try {
    const screen = Dimensions.get('screen');
    const scale = screen.scale || 1;
    return `${Math.round(screen.width * scale)}x${Math.round(screen.height * scale)}`;
  } catch {
    return undefined;
  }
};

const getDeviceLanguage = () => {
  try {
    const { getLocales } = require('expo-localization');
    const locales = getLocales();
    return locales?.[0]?.languageTag || undefined;
  } catch {
    return undefined;
  }
};

const buildBaseParams = () => {
  const now = new Date();
  const params = {
    idsite: SITE_ID,
    rec: '1',
    apiv: '1',
    _id: _visitorId,
    rand: Math.floor(Math.random() * 0x7fffffff).toString(),
    h: now.getHours().toString(),
    m: now.getMinutes().toString(),
    s: now.getSeconds().toString(),
    res: getScreenResolution(),
    ua: buildUserAgent(),
    lang: getDeviceLanguage(),
  };
  if (_userId) params.uid = _userId;
  for (const [idx, val] of Object.entries(_customDimensions)) {
    params[`dimension${idx}`] = val;
  }
  return params;
};

const encodeParams = (params) =>
  Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const analyticsPermissionStillCurrent = revision => Platform.OS !== 'ios'
  || (hasNativeAnalyticsConsent() && useNativeAnalyticsConsent.getState().revision === revision);

const dispatchOne = async (params, revision) => {
  try {
    if (!analyticsPermissionStillCurrent(revision)) return;
    const body = encodeParams({ ...buildBaseParams(), ...params });
    await fetch(MATOMO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch {
    // Analytics failures must never break the app
  }
};

const enqueue = (params) => {
  if (Platform.OS === 'ios' && !hasNativeAnalyticsConsent()) return;
  _eventQueue.push(params);
  if (!_dispatchTimer) startDispatchTimer();
};

const flushQueue = async () => {
  try {
    if (_eventQueue.length === 0) return;
    const batch = _eventQueue.splice(0);
    const revision = useNativeAnalyticsConsent.getState().revision;
    await ensureVisitorId();
    if (!analyticsPermissionStillCurrent(revision)) return;
    for (const params of batch) {
      await dispatchOne(params, revision);
    }
  } catch {
    // Never let analytics crash the app
  }
};

const startDispatchTimer = () => {
  if (_dispatchTimer) return;
  _dispatchTimer = setInterval(() => {
    flushQueue();
  }, DISPATCH_INTERVAL_MS);

  flushQueue();
};

const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

if (Platform.OS === 'ios') {
  useNativeAnalyticsConsent.getState().load();
  useNativeAnalyticsConsent.subscribe(state => {
    if (!state.allowed) {
      _eventQueue = [];
      if (_dispatchTimer) clearInterval(_dispatchTimer);
      _dispatchTimer = null;
    }
  });
}

/**
 * matomo.js swaps the bootstrap `_paq` array for a `{ push }` proxy the moment
 * it finishes loading, so an `Array.isArray` check stops matching exactly when
 * real user events start firing. Both shapes accept `push`; creating the array
 * when neither exists buffers events raised before the deferred loader runs
 * instead of dropping them.
 */
const matomoQueue = () => {
  if (typeof window === 'undefined') return null;
  if (!window._paq) window._paq = [];
  return typeof window._paq.push === 'function' ? window._paq : null;
};

// ---------------------------------------------------------------------------
// Public API — works on web (via _paq) and native (via HTTP Tracking API)
// ---------------------------------------------------------------------------
export const trackUser = (userId) => {
  if (isNative) {
    _userId = userId;
    return;
  }
  const paq = matomoQueue();
  if (paq) paq.push(['setUserId', userId]);
};

export const trackEvent = (category, action, name = null, value = null) => {
  if (isNative) {
    const params = {
      e_c: category,
      e_a: action,
      ca: '1',
      url: `${CONTENT_BASE}/event`,
    };
    if (name != null) params.e_n = String(name);
    if (value != null) params.e_v = String(value);
    enqueue(params);
    return;
  }
  const paq = matomoQueue();
  if (paq) paq.push(['trackEvent', category, action, name, value]);
};

export const trackPageView = (pageTitle = null, pageUrl = null) => {
  if (isNative) {
    const params = {
      url: pageUrl ? `${CONTENT_BASE}${pageUrl}` : `${CONTENT_BASE}/`,
    };
    if (pageTitle) params.action_name = pageTitle;
    enqueue(params);
    return;
  }
  const paq = matomoQueue();
  if (!paq) return;
  paq.push(['setCustomUrl', pageUrl || window.location?.pathname]);
  if (pageTitle) {
    paq.push(['setDocumentTitle', pageTitle]);
  }
  paq.push(['trackPageView']);
};

export const trackSearch = (keyword, category = null, resultCount = null) => {
  if (isNative) {
    const params = {
      search: keyword,
      url: `${CONTENT_BASE}/search`,
    };
    if (category != null) params.search_cat = String(category);
    if (resultCount != null) params.search_count = String(resultCount);
    enqueue(params);
    return;
  }
  const paq = matomoQueue();
  if (paq) paq.push(['trackSiteSearch', keyword, category, resultCount]);
};

/**
 * Matomo custom dimension slots. Our Matomo plan allows five visit-scoped
 * dimensions per site; we use four for durable segmentation and leave one
 * spare. Indices must match the "Visit" dimensions configured in Matomo —
 * see docs/ANALYTICS_EVENTS.md.
 */
export const MATOMO_DIMENSION = Object.freeze({
  USER_TIER: 1,
  PLATFORM: 2,
  LANDING_PATH: 3,
  CHECKOUT_SOURCE: 4,
});

export const setCustomDimension = (index, value) => {
  const idx = Number(index);
  if (!Number.isInteger(idx) || idx < 1) return;
  if (value == null || value === '') return;
  const stringValue = String(value);

  if (isNative) {
    _customDimensions[idx] = stringValue;
    return;
  }
  const paq = matomoQueue();
  if (paq) paq.push(['setCustomDimension', idx, stringValue]);
};

export const setExperimentDimensions = (_experiments) => {
  // Experiment assignments are tracked as events (`experiment/assigned`) only.
  // We no longer mirror them into Matomo custom dimensions — the plan's five
  // visit slots are reserved for user_tier, platform, landing_path, and
  // checkout_source (see MATOMO_DIMENSION).
};

/**
 * Record a Matomo Goal conversion. Goals are the unit the Matomo UI reports
 * revenue and conversion rate on, so the money events fire a goal in addition
 * to their event. Goal IDs are configured per-site — see
 * docs/ANALYTICS_EVENTS.md for the mapping and MATOMO_GOAL below.
 */
export const trackGoal = (goalId, revenue = null) => {
  const id = Number(goalId);
  if (!Number.isInteger(id) || id < 1) return;

  if (isNative) {
    const params = { idgoal: String(id), url: `${CONTENT_BASE}/goal` };
    if (revenue != null && Number.isFinite(Number(revenue))) {
      params.revenue = String(Number(revenue));
    }
    enqueue(params);
    return;
  }
  const paq = matomoQueue();
  if (!paq) return;
  if (revenue != null && Number.isFinite(Number(revenue))) {
    paq.push(['trackGoal', id, Number(revenue)]);
  } else {
    paq.push(['trackGoal', id]);
  }
};

export const trackExperimentAssignments = (experiments) => {
  if (!experiments || typeof experiments !== 'object') return;

  for (const [name, variant] of Object.entries(experiments)) {
    trackEvent('experiment', 'assigned', name, variant);
  }
};

// Force-dispatch any queued events (e.g. before app goes to background)
export const flushAnalytics = () => {
  if (isNative) flushQueue();
};

// Facebook and Reddit browser tracking utilities (trackFacebookEvent,
// trackRedditEvent) removed 2026-09 along with the pixels.

const WHOP_FIRST_PROMPT_KEY = 'whop_first_prompt';
const WHOP_PURCHASE_PREFIX = 'whop_purchase:';

/**
 * Browser-only Whop Pixel. The snippet in `+html.jsx` / `_layout.pug` defines
 * `window.whop` as a queue stub, so this is safe before `s.js` finishes loading.
 * No-ops on native and when the pixel is blocked.
 */
export const trackWhopEvent = (event, params) => {
  if (typeof window === 'undefined') return;
  const track = window.whop?.track;
  if (typeof track !== 'function' || !event) return;
  try {
    if (params && typeof params === 'object' && Object.keys(params).length > 0) {
      track(event, params);
    } else {
      track(event);
    }
  } catch (_) { /* never block the product on analytics */ }
};

export const trackWhopCompleteRegistration = () => {
  trackWhopEvent('complete_registration');
};

export const trackWhopFirstPrompt = (sentMessageCount) => {
  if (typeof window === 'undefined') return;
  if (sentMessageCount == null) return;
  if ((Number(sentMessageCount) || 0) > 0) return;
  try {
    if (sessionStorage.getItem(WHOP_FIRST_PROMPT_KEY)) return;
    sessionStorage.setItem(WHOP_FIRST_PROMPT_KEY, '1');
  } catch (_) { /* private mode / blocked storage */ }
  trackWhopEvent('first_prompt');
};

/**
 * Successful payment on any processor. Skip known-zero amounts (free trials /
 * fully discounted checkouts) so they do not skew ROAS. Unknown amounts still
 * fire so redirect/3DS success pages are not dropped.
 */
export const trackWhopPurchase = ({ value, currency = 'USD', id } = {}) => {
  if (typeof window === 'undefined') return;
  const amount = Number(value);
  const hasNumericValue = value != null && value !== '' && Number.isFinite(amount);
  if (hasNumericValue && amount <= 0) return;
  if (id) {
    try {
      const key = `${WHOP_PURCHASE_PREFIX}${id}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (_) { /* private mode / blocked storage */ }
  }
  if (hasNumericValue) {
    trackWhopEvent('purchase', { value: amount, currency: String(currency || 'USD').toUpperCase() });
    return;
  }
  trackWhopEvent('purchase');
};

// Ad click id persistence (persistRedditClickId / persistFacebookClickId) and
// the identifyFacebookUser / identifyRedditUser advanced-matching helpers were
// removed 2026-09 along with the Facebook and Reddit pixels.

// Collect basic client / device information to send with events or logs
export const getClientInfo = () => {
  const info = {
    platform: Platform.OS,
    platformVersion: Platform.Version,
    ...(workClientEnvironment() && { workClientEnvironment: workClientEnvironment() }),
  };

  try {
    info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    info.timezone = null;
  }

  // React Native Platform constants / flags
  try {
    const constants = Platform?.constants;
    if (constants) {
      const {
        isTesting,
        reactNativeVersion,
        uiMode,
        osVersion,
        systemName,
        interfaceIdiom,
        Version,
        Release,
        Serial,
        Fingerprint,
        Model,
        Brand,
        Manufacturer,
        ServerHost,
      } = constants;

      info.platformConstants = {
        isTesting,
        reactNativeVersion,
        uiMode,
        osVersion,
        systemName,
        interfaceIdiom,
        version: Version,
        release: Release,
        serial: Serial,
        fingerprint: Fingerprint,
        model: Model,
        brand: Brand,
        manufacturer: Manufacturer,
        serverHost: ServerHost,
      };
    }

    if (typeof Platform.isPad !== 'undefined') {
      info.isPad = Platform.isPad;
    }
    if (typeof Platform.isTesting !== 'undefined') {
      info.platformIsTesting = Platform.isTesting;
    }
  } catch (e) {
    console.log('Error getting Platform client info:', e);
  }

  try {
    if (Constants) {
      info.appOwnership = Constants.appOwnership;
      info.appVersion = Constants.nativeAppVersion;
      info.appBuild = Constants.nativeBuildVersion;
      info.expoRuntimeVersion = Constants.expoRuntimeVersion;
      info.releaseChannel = Constants.releaseChannel;
      info.sessionId = Constants.sessionId;
      info.deviceYearClass = Constants.deviceYearClass;
    }
  } catch (e) {
    console.log('Error getting Constants client info:', e);
  }

  try {
    if (Device) {
      info.deviceModelName = Device.modelName;
      info.deviceManufacturer = Device.manufacturer;
      info.osName = Device.osName;
      info.osVersion = Device.osVersion;
      info.deviceType = Device.deviceType;
      info.isDevice = Device.isDevice;
    }
  } catch (e) {
    console.log('Error getting Device client info:', e);
  }

  if (Platform.OS === 'web') {
    try {
      // Read the current panel session on every request; chat/login routing can
      // remove its URL parameters after the extension handshake establishes it.
      if (getBrowserPanelSession()) info.surface = 'browser-extension';
      info.userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
      info.language = typeof navigator !== 'undefined' ? navigator.language : undefined;
      info.languages = typeof navigator !== 'undefined' ? navigator.languages : undefined;
      info.webPlatform = typeof navigator !== 'undefined' ? navigator.platform : undefined;
      info.screen = typeof window !== 'undefined'
        ? {
            width: window.innerWidth,
            height: window.innerHeight,
            pixelRatio: window.devicePixelRatio,
          }
        : undefined;
    } catch (e) {
      console.log('Error getting web client info:', e);
    }
  }

  try {
    ensureDeviceObservation();
    const observation = getCachedDeviceObservation();
    if (observation) info.deviceObservation = observation;
  } catch (_) { /* observation is best-effort */ }

  return info;
};
